import { useState, useEffect } from 'react';
import { getAdminDashboard, updateAdminSettings } from '@/lib/api';
import { Package, Users, ShoppingCart, TrendingUp, Percent, Star, Crown, Gem } from 'lucide-react';

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [margin, setMargin] = useState('');
  const [rankDiscounts, setRankDiscounts] = useState({ pro: 20, gold: 25, grossiste: 30 });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    getAdminDashboard().then(r => {
      setData(r.data);
      setMargin(r.data.global_margin.toString());
      setRankDiscounts(r.data.rank_discounts || { pro: 20, gold: 25, grossiste: 30 });
    });
  }, []);

  const handleMarginUpdate = async () => {
    setSaving(true);
    setMsg('');
    try {
      const res = await updateAdminSettings({ global_margin: parseFloat(margin) });
      setMsg(res.data.message);
      getAdminDashboard().then(r => setData(r.data));
    } catch { setMsg('Erreur'); }
    setSaving(false);
  };

  const handleRankUpdate = async () => {
    setSaving(true);
    setMsg('');
    try {
      const res = await updateAdminSettings({ rank_discounts: {
        pro: parseFloat(rankDiscounts.pro) || 20,
        gold: parseFloat(rankDiscounts.gold) || 25,
        grossiste: parseFloat(rankDiscounts.grossiste) || 30
      }});
      setMsg(res.data.message);
      getAdminDashboard().then(r => setData(r.data));
    } catch { setMsg('Erreur'); }
    setSaving(false);
  };

  if (!data) return <div className="p-8 animate-pulse"><div className="h-8 bg-[#F8F9FA] w-48 mb-4" /><div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_,i)=><div key={i} className="h-24 bg-[#F8F9FA]" />)}</div></div>;

  return (
    <div data-testid="admin-dashboard" className="p-6 lg:p-8">
      <h1 className="text-2xl font-extrabold text-[#111] tracking-tight mb-6">Tableau de bord</h1>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10 mb-8">
        {[
          { label: 'Produits actifs', value: data.total_products, icon: Package, color: 'text-[#00A5FF]' },
          { label: 'Clients', value: data.total_clients, icon: Users, color: 'text-[#F5C400]', sub: `${data.pending_clients} en attente` },
          { label: 'Commandes', value: data.total_orders, icon: ShoppingCart, color: 'text-[#E8231B]' },
          { label: 'Revenue total', value: `${data.total_revenue.toFixed(0)} EUR`, icon: TrendingUp, color: 'text-green-600' },
        ].map((kpi, i) => (
          <div key={i} className="bg-white p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wider text-black/40 font-semibold">{kpi.label}</p>
              <kpi.icon size={16} className={kpi.color} />
            </div>
            <p className="text-2xl font-extrabold text-[#111]">{kpi.value}</p>
            {kpi.sub && <p className="text-xs text-[#F5C400] font-medium mt-1">{kpi.sub}</p>}
          </div>
        ))}
      </div>

      {/* Pricing Settings */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Global Margin */}
        <div className="border border-black/10 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Percent size={18} className="text-[#00A5FF]" />
            <h2 className="text-lg font-bold text-[#111]">Marge globale</h2>
          </div>
          <p className="text-xs text-black/40 mb-4">Prix public = Prix fournisseur x (1 + Marge %)</p>
          <div className="flex items-center gap-3">
            <div className="flex items-center border border-black/15">
              <input
                type="number" value={margin} onChange={(e) => setMargin(e.target.value)}
                data-testid="global-margin-input"
                className="w-20 px-3 py-2 text-sm font-bold text-center outline-none"
                step="1" min="0" max="200"
              />
              <span className="px-3 py-2 bg-[#F8F9FA] border-l border-black/15 text-sm font-medium">%</span>
            </div>
            <button
              onClick={handleMarginUpdate} disabled={saving}
              data-testid="update-margin-button"
              className="px-4 py-2 bg-[#00A5FF] text-white text-sm font-semibold rounded-sm hover:bg-[#0090e0] disabled:opacity-50 transition-colors duration-200"
            >
              Mettre à jour
            </button>
          </div>
        </div>

        {/* Rank Discounts */}
        <div className="border border-black/10 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Crown size={18} className="text-[#F5C400]" />
            <h2 className="text-lg font-bold text-[#111]">Remises par rang</h2>
          </div>
          <p className="text-xs text-black/40 mb-4">Réduction appliquée automatiquement au rang du client</p>
          <div className="space-y-3">
            {[
              { key: 'pro', label: 'PRO', icon: Star, color: 'text-[#00A5FF]', bg: 'bg-[#00A5FF]/10' },
              { key: 'gold', label: 'GOLD', icon: Crown, color: 'text-[#F5C400]', bg: 'bg-[#F5C400]/15' },
              { key: 'grossiste', label: 'GROSSISTE', icon: Gem, color: 'text-[#E8231B]', bg: 'bg-[#E8231B]/10' },
            ].map((rank) => (
              <div key={rank.key} className="flex items-center gap-3">
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-bold min-w-[100px] ${rank.bg} ${rank.color}`}>
                  <rank.icon size={12} />
                  {rank.label}
                </div>
                <div className="flex items-center border border-black/15">
                  <input
                    type="number"
                    value={rankDiscounts[rank.key]}
                    onChange={(e) => setRankDiscounts({...rankDiscounts, [rank.key]: e.target.value})}
                    className="w-16 px-2 py-1.5 text-sm font-bold text-center outline-none"
                    step="1" min="0" max="90"
                  />
                  <span className="px-2 py-1.5 bg-[#F8F9FA] border-l border-black/15 text-xs font-medium">%</span>
                </div>
              </div>
            ))}
            <button
              onClick={handleRankUpdate} disabled={saving}
              className="px-4 py-2 bg-[#F5C400] text-[#111] text-sm font-semibold rounded-sm hover:bg-[#e0b000] disabled:opacity-50 transition-colors duration-200 mt-2"
            >
              Appliquer les remises
            </button>
          </div>
        </div>
      </div>

      {msg && <p className="text-sm text-green-600 font-medium mb-4 p-3 bg-green-50 border border-green-200">{msg}</p>}

      {/* Recent Orders */}
      <div className="border border-black/10">
        <div className="p-4 border-b border-black/10">
          <h2 className="text-sm font-bold uppercase tracking-wider text-black/50">Commandes récentes</h2>
        </div>
        {data.recent_orders.length === 0 ? (
          <div className="p-8 text-center text-sm text-black/40">Aucune commande</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-[#F8F9FA] text-xs uppercase tracking-wider text-black/50">
                <th className="text-left px-4 py-2 font-semibold">N° Commande</th>
                <th className="text-left px-4 py-2 font-semibold">Client</th>
                <th className="text-left px-4 py-2 font-semibold">Statut</th>
                <th className="text-right px-4 py-2 font-semibold">Total</th>
                <th className="text-right px-4 py-2 font-semibold">Date</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_orders.map((o) => (
                <tr key={o.id} className="border-t border-black/5 hover:bg-[#F8F9FA] transition-colors duration-200">
                  <td className="px-4 py-3 text-sm font-medium">{o.order_number}</td>
                  <td className="px-4 py-3 text-sm text-black/60">{o.company_name}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 font-semibold rounded-sm">{o.status}</span></td>
                  <td className="px-4 py-3 text-sm font-bold text-right">{o.total?.toFixed(2)} EUR</td>
                  <td className="px-4 py-3 text-xs text-black/40 text-right">{new Date(o.created_at).toLocaleDateString('fr-BE')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
